import { FC, useState, useMemo, useEffect, useCallback } from 'react';
import { ErrorModel,Option } from 'Models';
import { ShowOnCondition, ValidateOnCondition, Validation } from '@cae/component-library';
import { styled } from '@mui/material/styles';
import { Autocomplete, Box, IconButton, TextField, Tooltip, tooltipClasses, TooltipProps, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';

export interface Form { [id: string]: FormField };
export type FieldType = "text" | "select" | "date" | "selectClipboard" | "custom";
export type KeyValueField = {
    key: string,
    value: string,
    message?: string
};
export interface BaseFormField {
    label: string,
    value: string,
    validations: Validation[],
    fieldType: FieldType,
    options?: Option[],
    multiple?: boolean,
    showOnCondition?: ShowOnCondition,
    validateOnCondition?: ValidateOnCondition,
    readOnly?: boolean,
    fullwidth?: boolean,
    placeHolder?: string,
    onChange?: (formFieldId: string, formField: FormField) => void,
    onInputChange?: (e: object, value: string, reason: string) => void,
    isAdmin?: boolean
}
export interface FormField extends BaseFormField{
    customField?: (props: FormFieldProps) => React.ReactNode
}

const FlexContainer = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
}));

const BootstrapTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.arrow}`]: {
        color: theme.palette.common.black,
    },
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.common.black,
        fontSize: 18,
    },
}));

export interface FormFieldProps {
    formFieldId: string;
    formField: FormField;
    error?: ErrorModel;
    onChange?: (formFieldId: string, formField: FormField) => void;
    onInputChange?: (formFieldId: string, value: string) => void;
    onClick?: (formFieldId: string, e: any) => void;
}
export const FormFieldComponent: FC<FormFieldProps> = ({ formFieldId, formField, error, onChange, onInputChange, onClick }) => {
    const [value, setValue] = useState<string>("");
    const { fieldType, label, readOnly } = formField;
    const options = useMemo(() => {
        return formField.options ?? [];
    },[formField.options]);

    useEffect(() => { 
        setValue(formField.value);
    }, [formField]);

    const handleOnChange = useCallback((value: string) => {
        formField.value = value;
        if(onChange) {
            onChange(formFieldId, formField);
        }
        setValue(value);
    }, [formField, formFieldId, onChange]);

    const handleOnInputChange = useCallback((value: string) => {
        if(onInputChange) {
            onInputChange(formFieldId, value);
        }
    }, [ formFieldId, onInputChange]);

    const handleOnClick = useCallback((e: any) => {
        if(onClick) {
            onClick(formFieldId, value);
        }
    }, [ formFieldId, value, onClick]);

    const displayField = useMemo((): React.ReactNode => ({
        "text": (
            <TextField
                fullWidth
                InputProps={readOnly ? {
                    readOnly: true,
                  } : {}}
                value={value}
                placeholder={formField.placeHolder ? formField.placeHolder : ""}
                onChange={(e) => handleOnChange(e.target.value)}
                helperText={error ? error.reasons[0] : ''}
                error={error ? true : false} variant="outlined" margin="dense"
            />
        ),
        "select": (
            <Autocomplete
                fullWidth
                options={ options }
                disableClearable={true}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                value={options.find(o => o.key === value) ?? {key: value, value: value}}
                onChange={(e, option) => handleOnChange(option?.key || "")}
                onInputChange = { (e, value, option) => handleOnInputChange(value) }
                getOptionLabel={(option) => option.value}
                renderInput={(params) => <TextField {...params} variant="outlined" margin="dense" helperText={error ? error.reasons[0] : ''} error={error ? true : false} />}
            />
        ),
        "selectClipboard": (
            <FlexContainer>
                <Autocomplete
                    fullWidth
                    id="debug"
                    options={ options }
                    disableClearable={true}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    value={options.find(o => o.key === value) ?? {key: value, value: value}}
                    onChange={(e, option) => handleOnChange(option?.key || "")}
                    onInputChange = { (e, value, option) => handleOnInputChange(value) }
                    getOptionLabel={(option) => option.value}
                    renderInput={(params) => <TextField {...params} variant="outlined" margin="dense" helperText={error ? error.reasons[0] : ''} error={error ? true : false} />}
                />
                <BootstrapTooltip title={formField.placeHolder? formField.placeHolder: "Used this button to Paste your selection in the dropdown"}  aria-label="add">
                    <IconButton style={{ padding: "0px 0px", minWidth: "35px", minHeight: "30px" }} onClick={(e) => handleOnClick( e )}>
                        <FontAwesomeIcon icon={faArrowRight} />
                    </IconButton>
                </BootstrapTooltip>
            </FlexContainer>
        ),
        "date": (
            <TextField
                fullWidth
                InputProps={readOnly ? {
                    readOnly: true,
                  } : {}}
                type="date"
                value={value}
                placeholder={formField.placeHolder ? formField.placeHolder : ""}
                onChange={(e) => handleOnChange(e.target.value)}
                helperText={error ? error.reasons[0] : ''}
                error={error ? true : false} variant="outlined" margin="dense"
            />
        ),
        "custom": formField.customField ? formField.customField({ formField, formFieldId, error, onChange: handleOnChange  }) : <></>
    }[fieldType]), [readOnly, value, error, options, fieldType, handleOnChange, handleOnInputChange, handleOnClick, formField, formFieldId]);

    return (
        <Box>
            <Typography>{label}</Typography>
            {displayField}
        </Box>
    );
}